// D79 — the typefaces, decided 12 August 2026, and deliberately a split.
//
// Marketing (aerios.at) sets its sans in Archivo. The PLATFORM keeps Inter,
// for two reasons and the first is a hard constraint rather than a preference:
// Archivo ships latin, latin-ext and vietnamese only — no Cyrillic, no Greek —
// and a product renders CUSTOMER data (tenant, site, park and operator names)
// that it does not choose and cannot constrain. Archivo would drop those names
// to a fallback face. Second, the platform's screens were designed and built
// on Inter; changing it re-opens every layout for no gain the product can feel.
//
// The Apple stack is dropped ENTIRELY. `-apple-system` / BlinkMacSystemFont /
// SF Pro Display / SF Pro Text / SF Mono are gone: leading with them rendered
// SF Pro on Mac and Inter everywhere else, i.e. two different typefaces
// depending on the operating system. That is what produced the split D79 had
// to resolve. `system-ui` survives only as a fallback for a FAILED FONT LOAD,
// never as a preference — the distinction is the whole point.
//
// The mono is now SHARED with the marketing system: figures, IDs, labels,
// table numerals and code all render in IBM Plex Mono on either brand. The
// sans differs by surface; the data typography does not, and for an inspection
// product mono is where precision is read. Self-hosted from the vendored
// design system (apps/web/src/generated/fonts/), SIL OFL 1.1.
//
// Sizes below are still the Apple-derived scale — Phase 3 of the design-system
// refactor moves them to the Aerios scale (D7). They are re-checked OPTICALLY
// rather than numerically when that happens: this scale is keyed to Apple's
// 17px iOS body, and the Aerios 16.5px sits in a different face with a
// different x-height, so matching numbers would not mean matching apparent size.
export const typography = {
  fontFamily: '"Inter Variable", Inter, system-ui, sans-serif',
  fontFamilyMono: '"IBM Plex Mono", ui-monospace, monospace',

  // D7 — the Aerios scale, 1.25 ratio, 4px-quantised. Sizes are re-checked
  // OPTICALLY rather than numerically: the old scale was keyed to Apple's 17px
  // iOS body, and 16.5px in a different face with a different x-height does not
  // mean "slightly smaller", it means "measured again".
  //
  // Legacy named keys are preserved so no consumer has to change in the same
  // commit, but they now resolve onto the real scale.
  size: {
    // legacy / dense product UI keys
    xs: 12, // the inline control's font (D77)
    sm: 13, // compact's one size below body-small — dense controls ONLY
    base: 14.5, // --body-small-size
    md: 16.5, // --body-size
    lg: 19, // --heading-3-size
    xl: 24, // --heading-2-size
    "2xl": 32, // --heading-1-size
    "3xl": 44, // --display-2 ceiling

    // Aerios semantic keys (preferred for new work)
    "display-xl": 60, // --display-1 ceiling
    "display-lg": 44,
    "display-md": 32,
    "display-sm": 24,
    "title-lg": 19,
    "title-md": 16.5,
    "body-lg": 20, // --body-lead-size
    "body-md": 16.5, // --body-size
    "body-sm": 14.5, // --body-small-size
    /**
     * 11px mono caps. NOT a shrunken body size — a different typographic role
     * (D6: mono carries labels and figures ONLY, never body copy or headings).
     * Pair it with `tracking.label`; capitals need MORE letter-space, not less.
     */
    "label-md": 11,
    "caption-lg": 13,
    "caption-sm": 12,
    "mono-md": 13,
    /** Mono 500, tabular, for a figure that is the point of its own tile. */
    metric: 40,
  },

  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    tight: 1.25, // --heading-1-lh
    base: 1.62, // --body-lh
    loose: 1.7,
  },

  // Negative tracking pulls a headline into one statement rather than loose
  // words. It applies to DISPLAY AND HEADING sizes only.
  tracking: {
    "display-xl": "-0.032em", // --display-1-ls
    "display-lg": "-0.028em", // --display-2-ls
    "display-md": "-0.022em", // --heading-1-ls
    "display-sm": "-0.018em", // --heading-2-ls
    "title-lg": "-0.01em", // --heading-3-ls
    "title-md": "-0.01em",
    "body-lg": "0em",
    "body-md": "0em",
    /**
     * POSITIVE. `--label-ls` is +0.14em, and this is the one that had been
     * wrong: D77 measured the platform's own level label at 14px/600/uppercase
     * with **-0.084px** tracking — capitals set with NEGATIVE letter-space, and
     * set larger than the 13px control they introduce and the 11px counts
     * beneath them. A label must not outrank its own data, and capitals need
     * more letter-space, not less.
     */
    "label-md": "0.14em",
    "caption-lg": "0em",
    "caption-sm": "0em",
    "mono-md": "0em",
  },

  /**
   * The label style, as one object — 11px mono caps at +0.14em. D77: labels do
   * NOT grow with density; this is the same at comfortable and compact.
   */
  label: {
    fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
} as const;

export type TypeStyleKey = keyof typeof typography.size;
